import { computed, type Signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import type { ApiResponse } from '@mb/common';
import { isBrowser } from '@mb/common/client';
import type { PersonSearchDto } from '../../shared';
import { personDisplayName } from '../../shared';

/**
 * Display names of an organization's persons, keyed by person id.
 *
 * Fetched separately from whatever rows carry the person ids — contracts,
 * bookings, accounts — and deliberately so: those rows are readable without a
 * name, and a failed person lookup must not take the page with it.
 *
 * Written once here because the first copy of it cost two bugs, both of which
 * are still worth knowing:
 *
 * - The endpoint wraps its rows in `{ success, data }`. Typed as a bare array,
 *   the loop below throws "not iterable", and an exception in one binding
 *   stops Angular updating the rest — which is how a whole table came out
 *   blank rather than merely nameless.
 * - The resource is read through `hasValue()`. `value()` rethrows while the
 *   resource is in an error state, so reading it directly turns a failed
 *   lookup into a broken page.
 *
 * Call it from an injection context and pass the organization as a signal; it
 * re-fetches when that changes and yields an empty map while nothing is known.
 */
export function injectPersonNamesById(
  organizationId: () => string | null,
): Signal<ReadonlyMap<string, string>> {
  const persons = httpResource<ApiResponse<PersonSearchDto[]>>(() => {
    const id = organizationId();
    if (!id || !isBrowser()) return undefined;
    return {
      url: `/api/v1/organization/organizations/${id}/persons`,
      method: 'GET',
      withCredentials: true,
    };
  });

  return computed(() => {
    const byId = new Map<string, string>();
    if (!persons.hasValue()) return byId;
    for (const person of persons.value()?.data ?? []) {
      byId.set(person.id, personDisplayName(person));
    }
    return byId;
  });
}
